import { validateProducts, validateFilters, validateSearchMeta, validateSortOptions } from '../utils/validation';
/**
 * Helper to extract image URL from various formats
 */
function extractImageUrl(imageData) {
    if (!imageData)
        return '/placeholder.jpg';
    if (Array.isArray(imageData)) {
        return imageData[0] || '/placeholder.jpg';
    }
    return imageData;
}
/**
 * Helper to extract array values from various formats
 */
function extractArrayValues(value) {
    if (!value)
        return [];
    if (Array.isArray(value)) {
        return value;
    }
    // Handle comma-separated values
    if (typeof value === 'string' && value.includes(',')) {
        return value.split(',').map(v => v.trim()).filter(Boolean);
    }
    return [value];
}
/**
 * Check if product is available based on various fields
 */
function checkAvailability(product) {
    // Check multiple fields for availability
    if (product.availability === false || product.inStock === false) {
        return 'out_of_stock';
    }
    if (product.quantity !== undefined && product.quantity <= 0) {
        return 'out_of_stock';
    }
    // Default to in stock if no negative indicators
    return 'in_stock';
}
export function mapUnbxdResponse(raw, options) {
    return {
        products: mapProducts(raw.response.products),
        meta: mapMeta(raw, options.page || 1),
        filters: mapFilters(raw.facets, options.filters),
        sorts: mapSorts()
    };
}
export function mapProducts(unbxdProducts) {
    const mappedProducts = unbxdProducts.map(product => {
        // Extract sizes from various sources
        const sizes = extractArrayValues(product.size);
        // Add sizes from variants if available
        if (product.variants && product.variants.length > 0) {
            product.variants.forEach(variant => {
                if (variant.size && !sizes.includes(variant.size)) {
                    sizes.push(variant.size);
                }
            });
        }
        // Extract colors
        const colors = extractArrayValues(product.color);
        // Add colors from variants if available
        if (product.variants && product.variants.length > 0) {
            product.variants.forEach(variant => {
                if (variant.color && !colors.includes(variant.color)) {
                    colors.push(variant.color);
                }
            });
        }
        return {
            id: product.uniqueId,
            title: product.title,
            handle: product.productUrl
                ? product.productUrl.split('/').pop() || product.uniqueId.toLowerCase().replace(/\s+/g, '-')
                : product.uniqueId.toLowerCase().replace(/\s+/g, '-'),
            brand: product.brand || '',
            color: colors[0] || '', // Primary color
            size: sizes,
            price: {
                regular: product.price,
                sale: product.salePrice
            },
            image: {
                url: extractImageUrl(product.imageUrl || product.imageUrls),
                alt: product.title
            },
            availability: checkAvailability(product),
            // Map additional fields if we extend the Product interface
            // sku: product.sku,
            // categories: product.categories,
            // tags: product.tags,
            // description: product.description,
        };
    });
    return validateProducts(mappedProducts, 'Unbxd');
}
export function mapFilters(unbxdFacets, currentFilters) {
    var _a, _b;
    const filters = [];
    // Handle structured facets format
    if (unbxdFacets && 'text' in unbxdFacets && 'range' in unbxdFacets) {
        // Map text/select filters
        if ((_a = unbxdFacets.text) === null || _a === void 0 ? void 0 : _a.list) {
            unbxdFacets.text.list.forEach((facet) => {
                const options = Array.isArray(facet.values[0])
                    ? facet.values.map(([name, count]) => {
                        var _a;
                        return ({
                            id: String(name),
                            label: String(name),
                            count: Number(count),
                            selected: ((_a = currentFilters === null || currentFilters === void 0 ? void 0 : currentFilters[facet.filterField]) === null || _a === void 0 ? void 0 : _a.includes(String(name))) || false,
                            value: String(name)
                        });
                    })
                    : facet.values.map((value) => {
                        var _a;
                        return ({
                            id: String(value.name),
                            label: String(value.name),
                            count: Number(value.count),
                            selected: ((_a = currentFilters === null || currentFilters === void 0 ? void 0 : currentFilters[facet.filterField]) === null || _a === void 0 ? void 0 : _a.includes(String(value.name))) || false,
                            value: String(value.name)
                        });
                    });
                filters.push({
                    id: facet.filterField,
                    label: facet.displayName,
                    type: 'select',
                    options
                });
            });
        }
        // Map range filters
        if ((_b = unbxdFacets.range) === null || _b === void 0 ? void 0 : _b.list) {
            unbxdFacets.range.list.forEach((facet) => {
                var _a;
                let currentMin = facet.values.start;
                let currentMax = facet.values.end;
                // Check for current price filter
                if (facet.facetName === 'price' && ((_a = currentFilters === null || currentFilters === void 0 ? void 0 : currentFilters.price) === null || _a === void 0 ? void 0 : _a[0])) {
                    const [min, max] = currentFilters.price[0].split(',').map(Number);
                    currentMin = min;
                    currentMax = max;
                }
                filters.push({
                    id: facet.facetName,
                    label: facet.displayName,
                    type: 'range',
                    min: facet.values.start,
                    max: facet.values.end,
                    gap: facet.values.gap,
                    currentMin,
                    currentMax
                });
            });
        }
    }
    else if (unbxdFacets && typeof unbxdFacets === 'object') {
        // Handle flat facets format (alternative response structure)
        Object.entries(unbxdFacets).forEach(([facetName, facetData]) => {
            if (facetData && typeof facetData === 'object') {
                // Check if it's a range facet
                if (facetData.start !== undefined && facetData.end !== undefined) {
                    filters.push({
                        id: facetName,
                        label: facetName.charAt(0).toUpperCase() + facetName.slice(1),
                        type: 'range',
                        min: facetData.start,
                        max: facetData.end,
                        gap: facetData.gap || 1
                    });
                }
                else if (facetData.values || Array.isArray(facetData)) {
                    // Text facet
                    const values = facetData.values || facetData;
                    const options = values.map((value) => {
                        var _a;
                        return ({
                            id: String(value.name || value),
                            label: String(value.name || value),
                            count: Number(value.count || 0),
                            selected: ((_a = currentFilters === null || currentFilters === void 0 ? void 0 : currentFilters[facetName]) === null || _a === void 0 ? void 0 : _a.includes(String(value.name || value))) || false,
                            value: String(value.name || value)
                        });
                    });
                    filters.push({
                        id: facetName,
                        label: facetName.charAt(0).toUpperCase() + facetName.slice(1),
                        type: 'select',
                        options
                    });
                }
            }
        });
    }
    return validateFilters(filters, 'Unbxd');
}
export function mapMeta(raw, page) {
    var _a;
    const pageSize = raw.response.rows || 24;
    return validateSearchMeta({
        totalResults: raw.response.numberOfProducts,
        totalPages: Math.ceil(raw.response.numberOfProducts / pageSize),
        currentPage: page,
        pageSize: pageSize,
        // Add spell check suggestions if available
        suggestions: (_a = raw.didYouMean) === null || _a === void 0 ? void 0 : _a.map(suggestion => ({
            text: suggestion,
            type: 'query'
        }))
    }, 'Unbxd');
}
export function mapSorts() {
    return validateSortOptions([
        { id: 'relevance', label: 'Relevance' },
        { id: 'price asc', label: 'Price: Low to High' },
        { id: 'price desc', label: 'Price: High to Low' },
        { id: 'title asc', label: 'Name: A to Z' },
        { id: 'title desc', label: 'Name: Z to A' },
        { id: 'popularity desc', label: 'Most Popular' },
        { id: 'rating desc', label: 'Highest Rated' }
    ], 'Unbxd');
}
